import { makeAutoObservable, runInAction } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAdminApi } from '@/services/AdminApi';
import { type ILogger } from '@/services/Logger';
import { type AdminSession, type AdminUser } from '@/domain/admin';

/**
 * The user-management ViewModel: lists users, selects one, and drives the mutations (assign/revoke role,
 * enable/disable, browse sessions, force-logout-everywhere). All behaviour is verified against `FakeAdminApi`.
 */
export class UsersStore {
  private _users: AdminUser[] = [];
  private _selected: string | null = null;
  private _sessions: AdminSession[] = [];
  private _loading = false;
  private _busy = false;
  private _error: string | null = null;
  private _notice: string | null = null;

  constructor(
    private api: IAdminApi,
    private logger: ILogger,
  ) {
    makeAutoObservable(this);
  }

  get users(): readonly AdminUser[] {
    return this._users;
  }
  get loading(): boolean {
    return this._loading;
  }
  get busy(): boolean {
    return this._busy;
  }
  get error(): string | null {
    return this._error;
  }
  get notice(): string | null {
    return this._notice;
  }
  get selectedUsername(): string | null {
    return this._selected;
  }
  get selected(): AdminUser | null {
    return this._users.find((u) => u.username === this._selected) ?? null;
  }
  get sessions(): readonly AdminSession[] {
    return this._sessions;
  }

  async load(): Promise<void> {
    this._loading = true;
    this._error = null;
    try {
      const users = await this.api.listUsers();
      runInAction(() => {
        this._users = users.slice().sort((a, b) => a.username.localeCompare(b.username));
      });
    } catch (e) {
      this.fail('load users', e);
    } finally {
      runInAction(() => (this._loading = false));
    }
  }

  select(username: string | null): void {
    this._selected = username;
    this._sessions = [];
    this._notice = null;
    this._error = null;
  }

  private async mutate(label: string, fn: () => Promise<void>): Promise<void> {
    this._busy = true;
    this._error = null;
    this._notice = null;
    try {
      await fn();
      runInAction(() => (this._notice = label));
      // Refresh the affected user so the view reflects the new state.
      await this.refreshSelected();
    } catch (e) {
      this.fail(label, e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private async refreshSelected(): Promise<void> {
    if (!this._selected) return;
    try {
      const u = await this.api.getUser(this._selected);
      runInAction(() => {
        this._users = this._users.map((x) => (x.username === u.username ? u : x));
      });
    } catch (e) {
      this.logger.warn('refresh user failed', e);
    }
  }

  assignRole(role: string): Promise<void> {
    const u = this._selected;
    const r = role.trim();
    if (!u || !r) return Promise.resolve();
    return this.mutate(`assigned role "${r}"`, () => this.api.assignRole(u, r));
  }
  revokeRole(role: string): Promise<void> {
    const u = this._selected;
    if (!u) return Promise.resolve();
    return this.mutate(`revoked role "${role}"`, () => this.api.revokeRole(u, role));
  }
  setEnabled(enabled: boolean): Promise<void> {
    const u = this._selected;
    if (!u) return Promise.resolve();
    return this.mutate(enabled ? 'user enabled' : 'user disabled', () => this.api.setEnabled(u, enabled));
  }
  forceLogout(): Promise<void> {
    const u = this._selected;
    if (!u) return Promise.resolve();
    return this.mutate('logged out everywhere', async () => {
      await this.api.logoutAllUser(u);
      const sessions = await this.api.listUserSessions(u);
      runInAction(() => (this._sessions = sessions));
    });
  }

  async loadSessions(): Promise<void> {
    const u = this._selected;
    if (!u) return;
    this._busy = true;
    try {
      const sessions = await this.api.listUserSessions(u);
      runInAction(() => (this._sessions = sessions));
    } catch (e) {
      this.fail('load sessions', e);
    } finally {
      runInAction(() => (this._busy = false));
    }
  }

  private fail(what: string, e: unknown): void {
    this.logger.error(`${what} failed`, e);
    runInAction(() => (this._error = `${what} failed: ${e instanceof Error ? e.message : String(e)}`));
  }
}

export const TOKEN_UsersStore = token<UsersStore>('UsersStore');
